import React, { useEffect, useMemo, useState } from "react"

function GroupJoinRequestModal({
  open,
  group,
  registeredName = "",
  isSubmitting = false,
  submitError = "",
  onClose,
  onSubmit,
}) {
  const normalizedRegisteredName = useMemo(() => {
    const name = String(registeredName || "").trim()
    return name || "My Registered Name"
  }, [registeredName])

  const [preferredNameMode, setPreferredNameMode] = useState("registered")
  const [akaName, setAkaName] = useState("")
  const [localError, setLocalError] = useState("")

  useEffect(() => {
    if (!open) return
    setPreferredNameMode("registered")
    setAkaName("")
    setLocalError("")
  }, [open, group?.id])

  if (!open) return null

  const handleSubmit = async () => {
    setLocalError("")
    if (preferredNameMode === "aka") {
      const trimmedAka = String(akaName || "").trim()
      if (trimmedAka.length < 2) {
        setLocalError("AKA name must be at least 2 characters.")
        return
      }
      if (trimmedAka.length > 80) {
        setLocalError("AKA name must be 80 characters or less.")
        return
      }
    }
    await onSubmit?.({ preferredNameMode, akaName: String(akaName || "").trim() })
  }

  return (
    <div className="fixed inset-0 z-[130] bg-black/45 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Join Group</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {group?.name || "Selected Group"}
              {group?.groupCode ? ` (${group.groupCode})` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-2.5 py-1 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-60"
          >
            Close
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-gray-600">
            Choose the name that should appear in this group.
          </p>

          <label className="flex items-start gap-2 p-3 rounded-xl border border-gray-200 bg-gray-50">
            <input
              type="radio"
              name="preferredName"
              checked={preferredNameMode === "registered"}
              onChange={() => setPreferredNameMode("registered")}
              className="mt-0.5"
              disabled={isSubmitting}
            />
            <span className="text-xs text-gray-700">
              <span className="font-semibold">1. RL registered name</span>
              <br />
              <span className="text-gray-500">
                It will appear as: <span className="font-semibold text-gray-700">{normalizedRegisteredName}</span>
              </span>
            </span>
          </label>

          <label className="block p-3 rounded-xl border border-gray-200 bg-gray-50">
            <div className="flex items-start gap-2">
              <input
                type="radio"
                name="preferredName"
                checked={preferredNameMode === "aka"}
                onChange={() => setPreferredNameMode("aka")}
                className="mt-0.5"
                disabled={isSubmitting}
              />
              <span className="text-xs text-gray-700 font-semibold">2. AKA</span>
            </div>
            <input
              type="text"
              value={akaName}
              onChange={(event) => setAkaName(event.target.value)}
              placeholder="Type your preferred AKA name"
              disabled={isSubmitting || preferredNameMode !== "aka"}
              className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-300 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-gray-100 disabled:text-gray-400"
            />
          </label>

          {!!localError && (
            <p className="text-xs text-red-600">{localError}</p>
          )}
          {!!submitError && (
            <p className="text-xs text-red-600">{submitError}</p>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg text-xs font-semibold text-white bg-sky-500 hover:bg-sky-600 disabled:opacity-60"
          >
            {isSubmitting ? "Sending..." : "Join"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default GroupJoinRequestModal

